import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import {
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'

import { HelpTip } from './ui/HelpTip'
import { api } from '../lib/api'
import { useFocusTrap } from '../lib/useFocusTrap'
import {
  detailFields,
  detailLabel,
  digestLinks,
  digestTextKey,
  expiredDays,
  noticeHref,
  ruleLabel,
  whyText,
  type NotificationReason,
} from './notificationDetail'
import {
  ModalBackdrop,
  btnGhost,
  btnPrimary,
  cardClass,
  dialogPanelClass,
  emptyStateClass,
  mutedTextClass,
} from './ui'
import { formatDateTime } from '../lib/dateFormat'

export interface Notification {
  id: string
  kind: string
  params: Record<string, string>
  link_entity: string | null
  link_id: string | null
  created_at: string
  read_at: string | null
  reason?: NotificationReason | null
}

interface NotificationPage {
  items: Notification[]
  next_cursor: string | null
  unread: number
}

export const NOTIFICATIONS_SECTION_ID = 'notifications'

const PAGE_SIZE = 20
const notificationsKey = ['notifications'] as const

const notificationsQueryFn = async ({ pageParam }: { pageParam: string | undefined }) =>
  (
    await api.get<NotificationPage>('/api/notifications', {
      params: { limit: PAGE_SIZE, cursor: pageParam },
    })
  ).data
const notificationsNextPage = (lastPage: NotificationPage) => lastPage.next_cursor ?? undefined

function useNotifications() {
  return useInfiniteQuery({
    queryKey: notificationsKey,
    queryFn: notificationsQueryFn,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: notificationsNextPage,
    staleTime: 30_000,
  })
}

export function unreadOf(data: InfiniteData<NotificationPage> | undefined): number {
  return data?.pages[0]?.unread ?? 0
}

/** ⛔ NOT the approvals counts (`useApprovalsAwaiting`, the Time and Leave items). */
export function useUnreadNotifications(): { count: number } {
  const client = useQueryClient()
  const mutating = useIsMutating()
  const wasMutating = useRef(mutating)
  useEffect(() => {
    if (wasMutating.current > 0 && mutating === 0) {
      void client.invalidateQueries({ queryKey: notificationsKey })
    }
    wasMutating.current = mutating
  }, [mutating, client])

  const query = useInfiniteQuery({
    queryKey: notificationsKey,
    queryFn: notificationsQueryFn,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: notificationsNextPage,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
  return { count: unreadOf(query.data) }
}

/** Pure cache patch for marking one notice read -- pulled out of the mutation so it can be tested without a QueryClient. */
export function applyRead(
  data: InfiniteData<NotificationPage> | undefined,
  updated: Notification,
): InfiniteData<NotificationPage> | undefined {
  if (!data) return data
  let wasUnread = false
  const pages = data.pages.map((page) => ({
    ...page,
    items: page.items.map((item) => {
      if (item.id !== updated.id) return item
      if (!item.read_at) wasUnread = true
      return updated
    }),
  }))
  if (!wasUnread || pages.length === 0) return { ...data, pages }
  pages[0] = { ...pages[0]!, unread: Math.max(0, pages[0]!.unread - 1) }
  return { ...data, pages }
}

/** Only `pages[0].unread` is authoritative (see `NotificationPage`), so later pages keep their stale count untouched. */
export function applyReadAll(
  data: InfiniteData<NotificationPage> | undefined,
  now: string,
): InfiniteData<NotificationPage> | undefined {
  if (!data) return data
  return {
    ...data,
    pages: data.pages.map((page, index) => ({
      ...page,
      unread: index === 0 ? 0 : page.unread,
      items: page.items.map((item) => (item.read_at ? item : { ...item, read_at: now })),
    })),
  }
}

function useMarkRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.post<Notification>(`/api/notifications/${id}/read`)).data,
    // Patches the cache in place rather than invalidating, so the unread count updates without a full
    // refetch.
    onSuccess: (updated) => {
      client.setQueryData<InfiniteData<NotificationPage>>(notificationsKey, (data) => applyRead(data, updated))
    },
  })
}

function useMarkAllRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<{ marked: number }>('/api/notifications/read-all')).data,
    onSuccess: () => {
      const now = new Date().toISOString()
      client.setQueryData<InfiniteData<NotificationPage>>(notificationsKey, (data) => applyReadAll(data, now))
    },
  })
}

export { entityRoute } from './notificationDetail'

/** Zero unread never shows it. */
export function shouldShowPopup(unread: number, lastSeenUnread: number | null): boolean {
  return unread > 0 && (lastSeenUnread === null || unread > lastSeenUnread)
}

function noticeText(t: (key: string, options?: Record<string, unknown>) => string, exists: (key: string) => boolean, n: Notification): string {
  const variant = variantText(t, n)
  if (variant !== null) return variant
  return exists(`notifications.kind.${n.kind}`) ? t(`notifications.kind.${n.kind}`, n.params) : t('notifications.kind.unknown')
}

function variantText(t: (key: string, options?: Record<string, unknown>) => string, n: Notification): string | null {
  const digestKey = digestTextKey(n)
  if (digestKey !== null) return t(digestKey, n.params)
  if (n.kind !== 'timesheet_rejected') return null
  switch (n.params?.variant) {
    case 'day':
      return t('notifications.kind.timesheet_rejected_day', n.params)
    case 'expired':
      return t('notifications.kind.timesheet_rejected_expired', n.params)
    case 'more':
      return t('notifications.kind.timesheet_rejected_more', n.params)
    default:
      return null
  }
}

function ExpiredDayList({ notification }: { notification: Notification }) {
  const { t } = useTranslation()
  const { lines, more } = expiredDays(notification, (kind) => (kind === 'Plan' ? t('time.kindPlan') : t('time.kindActual')))
  if (lines.length === 0) return null
  return (
    <ul className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
      {lines.map((line) => (
        <li key={line} className="break-words">
          {line}
        </li>
      ))}
      {more > 0 && <li>{t('notifications.morePeople', { count: more })}</li>}
    </ul>
  )
}

function NotificationRow({
  notification,
  onOpen,
  onRead,
}: {
  notification: Notification
  onOpen: (n: Notification, path?: string) => void
  onRead: (n: Notification) => void
}) {
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const exists = (key: string) => i18n.exists(key)
  const text = noticeText(t, exists, notification)
  const why = whyText(notification, t, exists)
  const details = detailFields(notification)
  const rule = ruleLabel(notification)
  const href = noticeHref(notification)
  const digest = digestLinks(notification)
  const unread = !notification.read_at
  const panelId = `notice-${notification.id}`
  const when = formatDateTime(new Date(notification.created_at), i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  return (
    <div className={`rounded-lg ${unread ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => {
          setExpanded((value) => !value)
          onRead(notification)
        }}
        className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40"
      >
        <span className="min-w-0 flex-1">
          <span
            className={`block ${unread ? 'font-medium text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}
          >
            {text}
          </span>
          <ExpiredDayList notification={notification} />
          {why && <span className="block text-xs text-slate-600 dark:text-slate-400">{why}</span>}
          <span className={unread ? 'text-xs text-slate-600 dark:text-slate-400' : mutedTextClass}>{when}</span>
        </span>
        <ChevronDown
          aria-hidden
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform dark:text-slate-400 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (
        <div id={panelId} className="space-y-2 px-2 pb-2">
          {(details.length > 0 || rule) && (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
              {details.map((field) => (
                <Fragment key={field.name}>
                  <dt className="text-slate-500 dark:text-slate-400">{detailLabel(t, field)}</dt>
                  <dd className="break-words text-slate-700 dark:text-slate-200">{field.value}</dd>
                </Fragment>
              ))}
              {rule && (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('notifications.sentBy')}</dt>
                  <dd className="break-words text-slate-700 dark:text-slate-200">{rule}</dd>
                </>
              )}
            </dl>
          )}
          {digest.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {digest.map((link) => (
                <button key={link.kind} type="button" onClick={() => onOpen(notification, link.href)} className={btnGhost}>
                  {link.kind === 'time'
                    ? t('notifications.digestTime', { n: link.count })
                    : t('notifications.digestLeave', { n: link.count })}
                </button>
              ))}
            </div>
          ) : (
            href && (
              <button type="button" onClick={() => onOpen(notification)} className={btnGhost}>
                {t('notifications.open')}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

export function NotificationsSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const query = useNotifications()
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const pages = query.data?.pages ?? []
  const items = pages.flatMap((page) => page.items)
  const unread = pages[0]?.unread ?? 0

  function read(n: Notification) {
    if (!n.read_at) markRead.mutate(n.id)
  }

  function open(n: Notification, to?: string) {
    read(n)
    const path = to ?? noticeHref(n)
    if (path) navigate(path)
  }

  return (
    <section
      id={NOTIFICATIONS_SECTION_ID}
      className={`${cardClass} flex max-h-[70vh] scroll-mt-16 flex-col gap-3`}
      aria-label={t('notifications.myTitle')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('notifications.myTitle')}
          </h2>
          <HelpTip label={t('notifications.myTitle')}>{t('notifications.hint')}</HelpTip>
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className={btnGhost}
          >
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {unread > 0 && (
        <p className="text-xs font-medium text-accent dark:text-blue-400">{t('notifications.unreadCount', { count: unread })}</p>
      )}

      <div className="flex-1 space-y-1 overflow-y-auto">
        {query.isLoading && <p className={mutedTextClass}>{t('action.loading')}</p>}
        {query.isError && <p className="text-xs text-reject">{t('notifications.error')}</p>}
        {!query.isLoading && !query.isError && items.length === 0 && (
          <p className={emptyStateClass}>{t('notifications.empty')}</p>
        )}
        {items.map((n) => (
          <NotificationRow key={n.id} notification={n} onOpen={open} onRead={read} />
        ))}
      </div>

      {query.hasNextPage && (
        <button
          type="button"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          className={btnGhost}
        >
          {t('notifications.loadMore')}
        </button>
      )}
    </section>
  )
}

const POPUP_STORAGE_KEY = 'trg_notif_last_seen_unread'

export function popupStorageKey(userId: string): string {
  return `${POPUP_STORAGE_KEY}:${userId}`
}

/** Parse a stored count; anything unreadable counts as "never shown". */
export function parseLastSeenUnread(raw: string | null): number | null {
  if (raw === null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

// sessionStorage can throw (a Teams webview, a private window): a failure reads as "never shown" and a
// dismiss still holds for the life of the page.
function readLastSeenUnread(key: string): number | null {
  try {
    return parseLastSeenUnread(sessionStorage.getItem(key))
  } catch {
    return null
  }
}

function writeLastSeenUnread(key: string, unread: number) {
  try {
    sessionStorage.setItem(key, String(unread))
  } catch {
  }
}

export function NotificationPopup({ userId, canViewAll = true }: { userId: string; canViewAll?: boolean }) {
  const navigate = useNavigate()
  const query = useNotifications()
  const markRead = useMarkRead()
  const storageKey = popupStorageKey(userId)
  const [lastSeenUnread, setLastSeenUnread] = useState<number | null>(() => readLastSeenUnread(storageKey))

  const unread = query.data?.pages[0]?.unread ?? 0
  const visible = !query.isLoading && shouldShowPopup(unread, lastSeenUnread)

  function markSeen() {
    setLastSeenUnread(unread)
    writeLastSeenUnread(storageKey, unread)
  }

  if (!visible) return null

  const items = (query.data?.pages[0]?.items ?? []).filter((n) => !n.read_at).slice(0, 5)

  function goTo(n: Notification) {
    markRead.mutate(n.id)
    markSeen()
    const path = noticeHref(n)
    if (path) navigate(path)
  }

  function viewAll() {
    markSeen()
    navigate({ pathname: '/me', hash: `#${NOTIFICATIONS_SECTION_ID}` })
  }

  return (
    <PopupDialog
      items={items}
      onOpen={goTo}
      onDismiss={markSeen}
      onViewAll={canViewAll ? viewAll : undefined}
    />
  )
}

function PopupWhy({ notification }: { notification: Notification }) {
  const { t, i18n } = useTranslation()
  const why = whyText(notification, t, (key) => i18n.exists(key))
  return why ? <span className="block text-xs text-slate-600 dark:text-slate-400">{why}</span> : null
}

// Its own component so `useFocusTrap` runs when the dialog actually mounts; called from
// NotificationPopup, whose first renders return null, the trap would arm against a null ref.
function PopupDialog({
  items,
  onOpen,
  onDismiss,
  onViewAll,
}: {
  items: Notification[]
  onOpen: (n: Notification) => void
  onDismiss: () => void
  onViewAll: (() => void) | undefined
}) {
  const { t, i18n } = useTranslation()
  const trapRef = useFocusTrap<HTMLDivElement>()
  return (
    <ModalBackdrop ref={trapRef} onClose={onDismiss} aria-label={t('notifications.popupTitle')}>
      <div className={dialogPanelClass}>
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{t('notifications.popupTitle')}</h2>
        <ul className="space-y-1">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => onOpen(n)}
                className="block w-full rounded-lg px-2 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40"
              >
                <span className="block">{noticeText(t, (key) => i18n.exists(key), n)}</span>
                <ExpiredDayList notification={n} />
                <PopupWhy notification={n} />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onDismiss} className={btnGhost}>
            {t('notifications.popupDismiss')}
          </button>
          {onViewAll && (
            <button type="button" onClick={onViewAll} className={btnPrimary}>
              {t('notifications.popupViewAll')}
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  )
}
